import React, { useEffect, useRef, useState } from 'react';
import markdown from '@buzzfeed/bfp-libs/src/markdown';
import styles from './studios-promo-unit.module.scss';
import {
  attachClientImpressionHandler,
  attachClientExternalLinkHandler
} from '../../hooks/analytics/client-event-tracking';
import { useAmazonSubtagModifier } from '../../hooks/useAmazonSubtagModifier';
import { CLUSTER } from '../../constants';

let promoConfigPromise = null;

function convertMarkdownToHtml(rawMarkdown = '') {
  if (!rawMarkdown) {
    return '';
  }

  return markdown(rawMarkdown, {images: false, strict: true});
}

function fetchPromoConfig() {
  if (!promoConfigPromise) {
    const apiHost = CLUSTER === 'prod' ? 'www.buzzfeed.com' : 'stage.buzzfeed.com';

    promoConfigPromise = fetch(`https://${apiHost}/studios-promo`)
      .then((res) => res.json())
      .catch(() => null);
  }
  return promoConfigPromise;
}

const StudiosPromoUnit = ({ buzz }) => {
  const studiosPromoTitle = useRef(null);
  const studiosPromoImageLink = useRef(null);
  const position = buzz && buzz.sub_buzzes ? buzz.sub_buzzes.length : 0;
  const clickRef = useAmazonSubtagModifier({ buzz });
  const [promoConfig, setPromoConfig] = useState({});

  useEffect(() => {
    fetchPromoConfig().then((data) => {
      const requiredFields = [
        data?.item_name,
        data?.copy_text,
        data?.cta_url,
        data?.image_url_desktop,
      ];

      const isValid = requiredFields.every(
        value => typeof value === 'string' && value.trim()
      );

      if (isValid) {
        setPromoConfig({
          ...data,
          image_alt: data.item_name.replace(/[^a-zA-Z0-9 ]/g, ' '),
        });
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // if promoConfig is not yet loaded, don't attach tracking handlers
    if (!promoConfig || !promoConfig.item_name) {
      return;
    }

    const detachExternalLinkTracking = []; // support multiple links in the markdown title
    let detachExternalImageLinkTracking;

    const studiosPromoLinks = studiosPromoTitle.current
      ? Array.from(studiosPromoTitle.current.querySelectorAll('a'))
      : [];

    const commonTrackingData = {
      item_name: promoConfig?.item_name,
      item_type: 'text',
      position_in_unit: position,
      unit_type: 'buzz_body',
      unit_name: buzz.id,
      subunit_type: 'component',
      subunit_name: 'studios_promo',
      target_content_type: 'url',
      target_content_id: promoConfig?.cta_url,
    };

    if (studiosPromoTitle && studiosPromoTitle.current) {
      attachClientImpressionHandler(studiosPromoTitle.current, buzz, {
        ...commonTrackingData,
        position_in_subunit: 0,
      });
    }

    // Apply link attributes to every markdown title link and register per-link click tracking.
    studiosPromoLinks.forEach((link, index) => {
      const linkHref = link.getAttribute('href') || promoConfig?.cta_url;
      link.setAttribute('rel', 'noopener noreferrer sponsored');
      link.setAttribute('data-affiliate', true);
      const detachTracking = attachClientExternalLinkHandler(link, buzz, {
        ...commonTrackingData,
        position_in_subunit: index,
        target_content_url: linkHref
      });

      if (typeof detachTracking === 'function') {
        detachExternalLinkTracking.push(detachTracking);
      }
    });

    if (studiosPromoImageLink && studiosPromoImageLink.current) {
      attachClientImpressionHandler(studiosPromoImageLink.current, buzz, {
        ...commonTrackingData,
        item_type: 'image',
        position_in_subunit: 1,
      });
    }

    if (studiosPromoImageLink && studiosPromoImageLink.current) {
      detachExternalImageLinkTracking = attachClientExternalLinkHandler(
        studiosPromoImageLink.current,
        buzz,
        {
          ...commonTrackingData,
          item_type: 'image',
          position_in_subunit: studiosPromoLinks.length,
          target_content_url: promoConfig?.cta_url
        }
      );
    }

    return () => {
      detachExternalLinkTracking.forEach((detachTracking) => {
        if (typeof detachTracking === 'function') {
          detachTracking();
        }
      });

      if (typeof detachExternalImageLinkTracking === 'function') {
        detachExternalImageLinkTracking();
      }
    };
  }, [buzz.id, position, promoConfig, studiosPromoImageLink, studiosPromoTitle]);

  /* eslint-disable react/jsx-no-target-blank */
  return !!promoConfig?.item_name && (
    <div className={styles.promoUnit} ref={clickRef}>
      <h2
        className={styles.promoUnitTitle}
        ref={studiosPromoTitle}
        dangerouslySetInnerHTML={{
          __html: convertMarkdownToHtml(promoConfig?.copy_text)
        }}
      />
      <div className={styles.promoUnitImage}>
        <a data-affiliate={true} target="_blank" rel="noopener noreferrer sponsored" ref={studiosPromoImageLink} href={promoConfig?.cta_url}>
          <img src={promoConfig?.image_url_desktop} alt={promoConfig?.image_alt} />
        </a>
      </div>
    </div>
  )
  /* eslint-enable react/jsx-no-target-blank */
}

export default StudiosPromoUnit;
